/**
 * Overlays PAEBM do mapa (M2) — geofence ZSS, mancha ZAS, sirenes, edificações
 * ZAS e pontos de encontro. Renderizado dentro do MapContainer; cada camada é
 * ligada/desligada via props.
 *
 * GUPM-195: edificações (sem moradores fixos → SEM PII) e pontos de encontro
 * vêm do banco (via `usePaebmMapa`). O popup da edificação NÃO tem telefone/
 * WhatsApp (não há morador) — a barreira LGPD do antigo `residenciaPoints` morreu.
 */
import { GeoJSON, Marker, Polygon, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { FeatureCollection } from 'geojson';
import { zssPlaceholderRing, type SirenePoint } from '../domain/paebm';
import { PONTO_ENCONTRO_COLOR, type PontoEncontro, type ZasEdificacao } from '@/domain';
import AssemblyPointIcon from './AssemblyPointIcon';
import { assemblyPointSvgString } from './assemblyPointSvg';

/** Anel placeholder da ZSS (estimativa ~15 km) — estático, calculado uma vez. */
const ZSS_RING = zssPlaceholderRing();

function emojiIcon(emoji: string, bg: string, size = 28): L.DivIcon {
  return L.divIcon({
    className: 'm2-overlay-pin',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:2px solid #0f1729;box-shadow:0 0 0 3px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-size:${size / 2}px">${emoji}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

/**
 * Marcador de ponto de encontro — pictograma ISO 7010 (assembly point) branco
 * num QUADRADO (cantos levemente arredondados) da cor do tipo (interno azul /
 * externo laranja), com borda escura + sombra pra destacar no satélite. Reúsa o
 * SVG do `assemblyPointSvgString` (fonte única — mesmo ícone da legenda/painel/popup).
 */
function assemblyIcon(color: string, size = 28): L.DivIcon {
  const svg = assemblyPointSvgString('#ffffff', Math.round(size * 0.64));
  return L.divIcon({
    className: 'm2-overlay-pin',
    html: `<div style="width:${size}px;height:${size}px;border-radius:5px;background:${color};border:2px solid #0f1729;box-shadow:0 0 0 2px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center">${svg}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

interface Props {
  sirenes: SirenePoint[];
  /** Edificações ZAS (sem moradores fixos — GUPM-195). */
  edificacoes: ZasEdificacao[];
  /** Pontos de encontro do PAEBM (Seção 8 — GUPM-195). */
  pontosEncontro: PontoEncontro[];
  mancha: FeatureCollection | null;
  /** ZSS — Zona de Salvamento Secundária (placeholder; geometria oficial a fornecer). */
  zss: boolean;
}

export default function MapOverlays({ sirenes, edificacoes, pontosEncontro, mancha, zss }: Props) {
  return (
    <>
      {/* ZSS — placeholder elíptico (~15 km); embaixo, ZAS/mancha por cima (fiel ao V8). */}
      {zss && (
        <Polygon
          positions={ZSS_RING}
          pathOptions={{ color: '#f97316', weight: 2.5, opacity: 0.9, fillColor: '#f97316', fillOpacity: 0.13, dashArray: '8 4' }}
        >
          <Popup>
            <strong style={{ color: '#f97316' }}>🟧 ZSS — Zona de Salvamento Secundária</strong>
            <div style={{ fontSize: 11, color: '#fed7aa', marginTop: 6 }}>⚠️ Dados preliminares.</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Estimativa ~15 km conforme PAEBM. Mancha oficial a ser fornecida pela Foscentro.
            </div>
          </Popup>
        </Polygon>
      )}
      {/* Mancha de inundação PAE (ZAS) — GeoJSON oficial IDE-Sisema/FEAM-MG */}
      {mancha && (
        <GeoJSON
          key="mancha-pae"
          data={mancha}
          style={{ color: '#ef4444', weight: 1.5, opacity: 0.9, fillColor: '#ef4444', fillOpacity: 0.22 }}
          onEachFeature={(_feature, layer) => {
            layer.bindPopup(
              '<strong style="color:#ef4444">🟥 ZAS — Mancha de inundação (PAE)</strong>'
              + '<div style="font-size:11px;color:#94a3b8;margin-top:6px">Barragem B · Patos de Minas</div>'
              + '<div style="font-size:10px;color:#64748b;margin-top:4px">Fonte: IDE-Sisema / FEAM-MG · ide_1903_mg_mancha_inundacao_pae_pol</div>',
              { className: 'lf-pop', maxWidth: 280 },
            );
          }}
        />
      )}

      {sirenes.map((s) => (
        <Marker key={s.id} position={[s.lat, s.lon]} icon={emojiIcon('📢', s.color)}>
          <Popup>
            <strong style={{ color: s.color }}>📢 {s.id}</strong>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Status: <strong style={{ color: s.color }}>{s.status === 'OK' ? '✅ OK' : '⚠️ FALHA'}</strong>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Último teste: {s.lastTest}</div>
          </Popup>
        </Marker>
      ))}

      {/* Edificações ZAS — SEM moradores fixos (sem PII, sem link de contato). */}
      {edificacoes.map((e) => (
        <Marker key={e.codigo} position={[e.lat, e.lon]} icon={emojiIcon('🏠', '#f97316', 34)}>
          <Popup>
            <strong style={{ color: '#f97316' }}>🏠 {e.label}</strong>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Sem moradores fixos</div>
            {e.distancia && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{e.distancia}</div>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              PE destino: <strong style={{ color: '#fff' }}>{e.pontoEncontro ?? '—'}</strong>
              {e.sinore && <span style={{ color: '#fde047' }}> · SINORE instalado</span>}
            </div>
            {e.ne3 && (
              <div style={{ fontSize: 10, color: '#fde047', fontWeight: 700, marginTop: 6 }}>⚠️ Evacuação em NE-3</div>
            )}
            <div style={{ fontSize: 10, color: '#22c55e', marginTop: 4, fontFamily: 'monospace' }}>
              {e.lat.toFixed(6)}, {e.lon.toFixed(6)} (PAEBM/SIRGAS2000)
            </div>
          </Popup>
        </Marker>
      ))}

      {/* Pontos de encontro — quadrado azul (interno) / laranja (externo). */}
      {pontosEncontro.map((p) => {
        const cor = PONTO_ENCONTRO_COLOR[p.tipo];
        return (
          <Marker key={p.codigo} position={[p.lat, p.lon]} icon={assemblyIcon(cor)}>
            <Popup>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <AssemblyPointIcon size={15} color={cor} />
                <strong style={{ color: '#fff' }}>{p.label}</strong>
                <span
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                    background: `${cor}1a`, color: cor, border: `1px solid ${cor}44`,
                  }}
                >
                  {p.tipo}
                </span>
              </div>
              {p.descricao && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{p.descricao}</div>
              )}
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                Área: {p.capacidade ?? '—'} · Rota: {p.tempoRota ?? '—'} · {p.nivel ?? '—'} · {p.populacaoEstimada ?? '—'} pessoa(s)
              </div>
              {p.acesso && (
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{p.acesso}</div>
              )}
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
